import { Link, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { useInvitationValidate } from "@/features/expert/api/hooks";
import "@/features/expert/i18n";

import { AuthScreen } from "./auth-screen";

/**
 * Public landing for an expert invitation link (`/experts/invitation/$token`).
 * Validates the token and either sends the invitee into signup (email prefilled)
 * or explains why the link no longer works — parity with the legacy
 * AcceptExpertInvitationView gate.
 */
export function ExpertInvitationPage() {
  const { t } = useTranslation();
  const { token } = useParams({ from: "/experts/invitation/$token" });
  const query = useInvitationValidate(token);

  if (query.isPending) {
    return (
      <AuthScreen>
        <p className="text-sm text-muted-foreground">{t("expert.loading")}</p>
      </AuthScreen>
    );
  }

  const status = query.data?.status ?? "invalid";
  const email = query.data?.email;

  if (status === "valid") {
    return (
      <AuthScreen>
        <div className="space-y-4">
          <h1 className="text-xl font-semibold">{t("expert.invite.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("expert.invite.validBody")}
          </p>
          <Button asChild>
            <Link
              to="/experts/signup"
              search={{ token, email: email ?? undefined }}
            >
              {t("expert.invite.cta")}
            </Link>
          </Button>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <div className="space-y-4">
        <h1 className="text-xl font-semibold">{t("expert.invite.title")}</h1>
        <p className="text-sm text-destructive">
          {t(`expert.invite.${status}`)}
        </p>
        <Button variant="outline" asChild>
          <Link to="/login">{t("expert.invite.toLogin")}</Link>
        </Button>
      </div>
    </AuthScreen>
  );
}
